import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { associateEmailWithTask } from '../../services/google/gmail';
import axiosInstance from '../../lib/api/axiosInstance';

interface Task {
  id: number;
  title: string;
  status: string;
  clientId: number;
  clientName: string;
}

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
}

interface TaskResponse {
  id: number;
  title: string;
  status: string;
  clientId: number;
  clientName: string;
  // autres propriétés potentielles
}

interface TaskAssociationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  emailId: string;
  emailSubject: string;
}

export const TaskAssociationModal: React.FC<TaskAssociationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  emailId,
  emailSubject,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('existing');
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [clients, setClients] = useState<User[]>([]);
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // Pour la nouvelle tâche
  const [newTaskTitle, setNewTaskTitle] = useState(emailSubject || '');
  const [selectedClientId, setSelectedClientId] = useState<string>('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Récupérer les tâches
        const tasksResponse = await axiosInstance.get<Task[]>('/tasks');
        setTasks(tasksResponse.data);
        
        // Récupérer les utilisateurs (remplace l'endpoint clients)
        const usersResponse = await axiosInstance.get<User[]>('/users', {
          params: { role: 'CLIENT' } // Filtrer par rôle CLIENT
        });
        setClients(usersResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        setErrorMessage(t('tasks.fetchError', 'Erreur lors de la récupération des données'));
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen, t]);

  const handleAssociateExisting = async () => {
    if (!selectedTaskId) {
      setErrorMessage(t('tasks.selectTask', 'Veuillez sélectionner une tâche'));
      return;
    }

    setLoading(true);
    try {
      await associateEmailWithTask(emailId, parseInt(selectedTaskId), emailSubject);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error associating email with task:', error);
      setErrorMessage(t('tasks.associationError', 'Erreur lors de l\'association de l\'email à la tâche'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAndAssociate = async () => {
    if (!newTaskTitle) {
      setErrorMessage(t('tasks.titleRequired', 'Veuillez entrer un titre pour la tâche'));
      return;
    }

    if (!selectedClientId) {
      setErrorMessage(t('tasks.clientRequired', 'Veuillez sélectionner un client'));
      return;
    }

    setLoading(true);
    try {
      // Créer la nouvelle tâche
      const newTaskResponse = await axiosInstance.post<TaskResponse>('/tasks', {
        title: newTaskTitle,
        status: 'TODO',
        clientId: parseInt(selectedClientId)
      });

      // Associer l'email à la nouvelle tâche
      await associateEmailWithTask(emailId, newTaskResponse.data.id, emailSubject);
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error creating task and associating email:', error);
      setErrorMessage(t('tasks.createAssociateError', 'Erreur lors de la création de la tâche et de l\'association de l\'email'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (activeTab === 'existing') {
      handleAssociateExisting();
    } else {
      handleCreateAndAssociate();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('tasks.associateEmail', 'Associer l\'email à une tâche')}</DialogTitle>
          <DialogDescription>
            {t('tasks.associateEmailDesc', 'Sélectionnez une tâche existante ou créez-en une nouvelle.')}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="existing" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="existing">{t('tasks.existingTask', 'Tâche existante')}</TabsTrigger>
            <TabsTrigger value="new">{t('tasks.newTask', 'Nouvelle tâche')}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="existing" className="space-y-4 mt-4">
            {loading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="task">{t('tasks.selectTask', 'Sélectionner une tâche')}</Label>
                  <Select value={selectedTaskId} onValueChange={setSelectedTaskId}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('tasks.selectTaskPlaceholder', 'Sélectionner une tâche')} />
                    </SelectTrigger>
                    <SelectContent>
                      {tasks.length === 0 ? (
                        <SelectItem value="no-tasks" disabled>
                          {t('tasks.noTasks', 'Aucune tâche disponible')}
                        </SelectItem>
                      ) : (
                        tasks.map((task) => (
                          <SelectItem key={task.id} value={task.id.toString()}>
                            {task.title} - {task.clientName}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="new" className="space-y-4 mt-4">
            {loading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">{t('tasks.taskTitle', 'Titre de la tâche')}</Label>
                  <Input
                    id="title"
                    value={newTaskTitle}
                    onChange={(e) => setNewTaskTitle(e.target.value)}
                    placeholder={t('tasks.taskTitlePlaceholder', 'Entrez le titre de la tâche')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client">{t('tasks.client', 'Client')}</Label>
                  <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('tasks.selectClientPlaceholder', 'Sélectionner un client')} />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.length === 0 ? (
                        <SelectItem value="no-clients" disabled>
                          {t('tasks.noClients', 'Aucun client disponible')}
                        </SelectItem>
                      ) : (
                        clients.map((client) => (
                          <SelectItem key={client.id} value={client.id.toString()}>
                            {client.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {errorMessage && (
          <div className="text-destructive text-sm">{errorMessage}</div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('common.processing', 'Traitement...')}
              </>
            ) : (
              t('common.confirm', 'Confirmer')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
